/**
 * ValidationErrors component: displays table column names where there are validation errors
 * 
 * @param {Object[]} validationErrors - array of errors with this format:  {row: 1, column: "Site_Name"}
 * @param {Object} columnNameMap - maps database key names to formatted column names, like: {"Site_Name": "Site Name"}
 * @returns {JSX.Element}
 * 
 * @category Components - UI
 */
export const ValidationErrors = ({ validationErrors, columnNameMap }) => {
  if (!validationErrors || validationErrors.length === 0) {
    return null;
  }

  const columnNamesOnly = validationErrors.map(error => error.column);
  const columnNamesNoDuplicates = Array.from(new Set(columnNamesOnly));

  const errorsByColumn = validationErrors.reduce((acc, { row, column }) => {
    acc[column] ||= [];
    acc[column].push(row + 1); // +1 if your table is 1-indexed for users
    return acc;
  }, {});

  // Custom message map for specific columns
  const customMessages = {
    OrderNum: "There are duplicate Order Numbers. Please ensure all are unique.",
    US_Site: "US Site appears incorrect. Please validate the segment.",
    DS_Site: "DS Site appears incorrect. Please validate the segment.",
    
  };

  return (
    <div className="my-6">
      <span className="text-red-500">
        <i className="fa-solid fa-circle-exclamation mr-2"></i>
        {columnNamesNoDuplicates.length === 1 ? "There's an issue in this column:" : "There are issues in these columns:"}
      </span>

      <ul className="mt-2 ml-4 list-disc text-red-500">
        {columnNamesNoDuplicates.map((columnName, i) => {
          
          const rowRanges = formatRowRanges(errorsByColumn[columnName] || []); //get rows where errors exist, grouped by column, display for user

          return (
            <li key={`error-${columnName}-${i}`}>
              <strong>{columnNameMap[columnName] || columnName}:</strong>{" "}

              {customMessages[columnName] || "This field must be filled out or corrected."}

              {rowRanges && (
                <span className="ml-1 text-sm opacity-90">
                  (rows {rowRanges})
                </span>
              )}
            </li>
          );
        })}
      </ul>

      <p className="text-red-500 mt-2">See highlighted boxes above.</p>
    </div>
  );
};

/**
 * Groups rows with errors into a string of ranges. Example output: 1-25, 100-13, 3450-3455
 */
const formatRowRanges = (rows) => {
  if (!rows.length) return "";

  const sorted = [...rows].sort((a, b) => a - b);
  const ranges = [];

  let start = sorted[0];
  let prev = sorted[0];

  for (let i = 1; i <= sorted.length; i++) {
    const current = sorted[i];

    if (current === prev + 1) {
      prev = current;
    } else {
      ranges.push(
        start === prev ? `${start}` : `${start}–${prev}`
      );
      start = current;
      prev = current;
    }
  }

  return ranges.join(", ");
};
